import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink, AlertTriangle } from 'lucide-react';
import { api } from '../../services/apiService';
import { billingApi } from '../../services/billingApi';
import BilledVsEnabledPanel from './BilledVsEnabledPanel';
import { RubyLoader } from '../RubyLoader';

/** The super-admin theme's one accent — ruby on white, never rose, never purple. */
const RUBY = '#DC143C';

interface Props {
  tenantId: string;
  open: boolean;
  onClose: () => void;
  /**
   * Fires after a successful "Run activation" — the same `onRan` name
   * `BilledVsEnabledPanel` already uses (Task 5), reused rather than adding a
   * second callback mechanism. Optional: `OrderDetail.tsx` passes it to
   * refresh its OWN copy of the activation runs (so the `packages_activated`
   * timeline pill updates without a full page reload); a host with no such
   * state of its own can simply omit it.
   */
  onRan?: () => void;
}

/**
 * THE QUICK VIEW an operator opens from a red `packages_activated` stage on
 * an order (OrderDetail.tsx) — without leaving the order screen. Reusable
 * from TenantProfile.tsx too, though that page already mounts Task 5's
 * `BilledVsEnabledPanel` directly on its Packages tab and does not need this
 * wrapper.
 *
 * Fetches its own data rather than depending on a host page's state, because
 * its two mount points do not share one: TenantProfile already has an
 * `auditLogs` fetch loaded for its own tabs; OrderDetail has nothing like it.
 * No new endpoint — same three reads TenantProfile already makes:
 *   - `api.superAdmin.getTenantPackages` for `tenant.enabledPackages` (the
 *     source of truth) and the id → name map for the catalog.
 *   - `billingApi.getActivePlanAssignment` for what was actually billed.
 *   - `api.superAdmin.getTenantAuditLog`, filtered client-side for
 *     `kind === 'action_block_run'` rows — the last runs.
 *
 * Rendering (the Billed vs Enabled table, the last-5 runs with per-step
 * results, the `degraded` shrinkage-guard message in full, and the "Run
 * activation" write) is entirely `BilledVsEnabledPanel` — this component only
 * supplies the data and the modal chrome around it, so there is exactly one
 * place that logic lives.
 */
const PackageActivationQuickView: React.FC<Props> = ({ tenantId, open, onClose, onRan }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [enabledPackages, setEnabledPackages] = useState<string[]>([]);
  const [packageNames, setPackageNames] = useState<Record<string, string>>({});

  const [billedPackages, setBilledPackages] = useState<string[] | null>(null);
  const [billedLoading, setBilledLoading] = useState(false);
  const [billedError, setBilledError] = useState<string | null>(null);

  const [lastRuns, setLastRuns] = useState<any[]>([]);

  const loadPackages = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.superAdmin.getTenantPackages(tenantId);
      setTenantName(res.tenant?.name || null);
      setEnabledPackages(res.tenant?.enabledPackages || []);
      const names: Record<string, string> = {};
      (res.allPackages || []).forEach((p) => { names[p.id] = p.name; });
      setPackageNames(names);
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to load this tenant’s enabled packages.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Same client, same `?status=active` read PlanAssignmentBuilder and Task
  // 5's panel on TenantProfile use — see billingApi.getActivePlanAssignment.
  const loadBilled = useCallback(async () => {
    if (!tenantId) return;
    setBilledLoading(true);
    setBilledError(null);
    try {
      const res = await billingApi.getActivePlanAssignment(tenantId);
      const row = (res.items || [])[0];
      setBilledPackages(row ? Array.from(new Set(row.planPackageIds || [])) : []);
    } catch (err: any) {
      // "Could not read" and "there is none" are different facts — never
      // collapse a failed fetch into an empty billed set.
      setBilledPackages(null);
      setBilledError(err?.message || 'The active plan assignment could not be read.');
    } finally {
      setBilledLoading(false);
    }
  }, [tenantId]);

  // `limit: 200`, not the endpoint's default 50 — rows are filtered
  // client-side for `kind === 'action_block_run'` AFTER the server applies
  // the limit, so a narrow window can crowd a real run out of a page of
  // unrelated audit history. `OrderDetail.tsx`'s own fetch uses the same
  // limit for the same reason — the two surfaces must not disagree about
  // whether a run exists.
  const loadRuns = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await api.superAdmin.getTenantAuditLog(tenantId, 200);
      const events: any[] = Array.isArray((res as any)?.events) ? (res as any).events : [];
      setLastRuns(events.filter((e: any) => e?.kind === 'action_block_run').slice(0, 5));
    } catch {
      // The runs list is a convenience, not load-bearing — an audit-log
      // outage should not stop the Billed vs Enabled comparison from showing.
      setLastRuns([]);
    }
  }, [tenantId]);

  useEffect(() => {
    if (!open || !tenantId) return;
    loadPackages();
    loadBilled();
    loadRuns();
  }, [open, tenantId, loadPackages, loadBilled, loadRuns]);

  // After "Run activation" — refresh everything it could have changed:
  // enabled packages, and the run history it just appended to. `onRan?.()`
  // is best-effort and un-awaited: OrderDetail's own refresh (see
  // `loadActivationRuns`) is already fail-open on its own, and a slow or
  // failed host refresh must never hold up this modal's own re-render.
  const handleRan = useCallback(async () => {
    await Promise.all([loadPackages(), loadBilled(), loadRuns()]);
    onRan?.();
  }, [loadPackages, loadBilled, loadRuns, onRan]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 p-4 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-stone-900/5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* THE HEADER IS THE ONLY THING PINNED. It used to be `sticky` inside
            the same scroller as the body, which left it re-painting its own
            rounded corner over scrolled content; the card is a column now and
            only the body scrolls. The ruby hairline is the super-admin
            accent — the theme's one brand colour, on white. */}
        <div className="shrink-0 border-b border-stone-100 px-5 pb-4 pt-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <span
                className="mb-1.5 block h-[3px] w-8 rounded-full"
                style={{ background: RUBY }}
                aria-hidden="true"
              />
              <h2 className="text-[17px] font-bold tracking-tight text-stone-900">Package activation</h2>
              <p className="truncate text-[13px] text-stone-500">{tenantName || tenantId}</p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="-mr-1 shrink-0 rounded-lg p-2 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pt-4">
          {loadError && (
            <div className="mx-5 mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] leading-relaxed text-red-700">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>{loadError}</span>
            </div>
          )}

          {loading && !loadError ? (
            <RubyLoader variant="section" className="py-14" />
          ) : (
            /* `embedded` — the panel's own card chrome is dropped here, because
               this modal IS the card. Two frames a few pixels apart was the
               doubled edge under the header. */
            <BilledVsEnabledPanel
              embedded
              tenantId={tenantId}
              enabledPackages={enabledPackages}
              billedPackages={billedPackages}
              billedLoading={billedLoading}
              billedError={billedError}
              packageNames={packageNames}
              lastRuns={lastRuns}
              onRan={handleRan}
            />
          )}
        </div>

        {/* The way out of the modal is a footer, not a link floating after the
            content — at fourteen packages it had scrolled far off the screen. */}
        <div className="shrink-0 border-t border-stone-100 bg-stone-50/70 px-5 py-3">
          <button
            onClick={() => { onClose(); navigate(`/tenant/${tenantId}?tab=packages`); }}
            className="inline-flex min-h-[36px] items-center gap-1.5 text-[13px] font-bold transition-opacity hover:opacity-75"
            style={{ color: RUBY }}
          >
            Open Plan &amp; Packages
            <ExternalLink size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PackageActivationQuickView;
